import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import axios from "axios";
import { toast } from "react-toastify";
import Select, { components } from "../../components/select";
import { AsyncSelect } from "../../components/select";

import { useLanguage } from "../../context/languageContext";
import { fetchAllRecommendersByLangOptions, searchSeriesOptions } from "../../helper";
import Button from "../../components/button";
import EditPageHeader, { EditPageLayout, HeaderDeleteButton } from "../../components/editPageHeader";
import { useConfirm } from "../../components/confirmDialog";
import SortablePeopleList from "../../components/sortablePeople";
import DayEditPopup from "../../components/dayEditPopup";
import WeekSchedule from "../../components/weekSchedule";
import styles from "./PicksSections.module.scss";
import { titleMissing, TITLE_HINT } from "../../helper/sectionTitles";

const API = process.env.REACT_APP_API_ROOT;

const OPTION_STYLES = {
    container: { display: "flex", alignItems: "center", gap: "10px" },
    image: { width: "30px", height: "30px", objectFit: "cover", borderRadius: "50%" },
    name: { margin: 0 },
};

// Module scope so it isn't a new component type each render (which would
// remount the menu mid-interaction).
const Option = React.memo((props) => {
    const { image, label } = props.data || {};
    return (
        <components.Option {...props}>
            <div style={OPTION_STYLES.container}>
                {image && <img src={image} alt={label} style={OPTION_STYLES.image} />}
                <p style={OPTION_STYLES.name}>{label}</p>
            </div>
        </components.Option>
    );
});

const SELECT_COMPONENTS = { Option };

const EMPTY = {
    key: "",
    layout: "rail",
    itemType: "recommender",
    // "manual" = the hand-picked list below; "mostRecommended" = the server
    // works the books out itself (see the Books-from choice in the form).
    source: "manual",
    minRecommenders: 5,
    isActive: true,
    translations: [],
    items: [],
};

/**
 * Create or edit a Picks section. `sectionId` null => create. Rails use a
 * drag-to-reorder list for members; the banner uses a single picker.
 */
const AddEditPopup = (props) => {
    const { adminInfo, selectedLang, languages, languageLabels } = useLanguage();
    // `asPage` renders the same form full-width on its own route instead of in a
    // modal. A hundred book covers in a grid needs the whole screen; a
    // recommender section with four people is still fine in a dialog.
    const { isVisible, handleCloseModal, sectionId, onSuccess, asPage = false } = props;

    const isCreate = !sectionId;

    const [values, setValues] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [isSubmit, setIsSubmit] = useState(false);
    const [recommenders, setRecommenders] = useState([]);
    // Books are NOT preloaded. There are ~11,000 of them against a few hundred
    // people, so they are searched on demand; `bookCache` keeps labels for
    // whatever has been chosen or loaded so a selected book never renders blank.
    const [bookCache, setBookCache] = useState({});
    const [titleLang, setTitleLang] = useState(selectedLang || "en");
    // The source as last saved — the preview of today's books only exists for
    // a section the server already treats as automatic.
    const [savedSource, setSavedSource] = useState("manual");
    // The date open in the day editor, or null.
    const [editingDay, setEditingDay] = useState(null);
    // Bumped when the day editor saved something, so the week view refetches.
    const [scheduleVersion, setScheduleVersion] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const confirm = useConfirm();

    useEffect(() => {
        if (!isVisible || !adminInfo?.token) return;
        (async () => {
            const options = (await fetchAllRecommendersByLangOptions(selectedLang, adminInfo.token)) || [];
            setRecommenders(options.map((r) => ({ label: r.recommenderName, value: r.recommenderId, image: r.image })));
        })();
    }, [isVisible, adminInfo, selectedLang]);

    useEffect(() => {
        if (!isVisible) return;
        setTitleLang(selectedLang || "en");
        setErrors({});
        if (sectionId) {
            fetchSection(sectionId);
        } else {
            setValues(EMPTY);
            setSavedSource("manual");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, sectionId]);

    const fetchSection = async (id) => {
        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.get(`${API}/api/admin/picks/sections/get/${id}`, { headers });
            if (response.status) {
                setValues({ ...EMPTY, ...(response?.data || {}) });
                setSavedSource(response?.data?.source || "manual");
            } else {
                toast.error(response.message || "Could not load this section.");
            }
        } catch (error) {
            console.error("fetchSection catch error::", error);
            toast.error("Could not load this section.");
        }
    };

    const isBanner = values.layout === "banner";
    const isBookSection = values.itemType === "book";
    const isAuto = isBookSection && !isBanner && values.source === "mostRecommended";

    // Reuses the summary picker's endpoint, which already excludes books that
    // have a summary — so this list is "books not yet covered", as intended.
    const loadBookOptions = async (input) => {
        if (!input || input.trim().length < 2) return [];
        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.get(
                // includeSummarized: a picks section is a shelf of library books, so a
                // book belongs here whether or not it also has a summary — "Atomic
                // Habits" can sit in Top 100 and still have one. The summary form calls
                // the same endpoint without the flag, where filtering them out is what
                // stops two summaries being written for one book.
                `${API}/api/admin/books/getall/booknames/${encodeURIComponent(input.trim())}?includeSummarized=1`,
                { headers }
            );
            const options = (response?.data || []).map((b) => ({
                value: b.bookId,
                // Two books can share a title, so the author is part of the
                // label — otherwise picking between them is guesswork.
                label: b.authorName ? `${b.bookName} — ${b.authorName}` : b.bookName,
                image: b.coverImage,
            }));
            setBookCache((prev) => ({
                ...prev,
                ...Object.fromEntries(options.map((o) => [o.value, o])),
            }));

            // A series is a set of books, so it belongs on the same shelf and
            // in the same search. Grouped rather than mixed in: the two are
            // different kinds of thing, and a card that says "9 books" next to
            // one that is a book needs the heading to explain itself.
            const series = await loadSeriesOptions(input);

            return series.length
                ? [
                    { label: "Books", options },
                    { label: "Series", options: series },
                ]
                : options;
        } catch (error) {
            console.error("loadBookOptions error::", error);
            return [];
        }
    };

    /// The admin series list, filtered by name — the same endpoint the Series
    /// page uses, so a series found here is a series that exists there.
    const loadSeriesOptions = async (input) => {
        const options = await searchSeriesOptions(input, adminInfo.token);
        setBookCache((prev) => ({
            ...prev,
            ...Object.fromEntries(options.map((o) => [o.value, o])),
        }));
        return options;
    };
    const titleForLang = (langCode) =>
        (values.translations || []).find((t) => t.langCode === langCode)?.title || "";

    const handleChangeTitle = (langCode, title) => {
        setValues((prev) => {
            const translations = [...(prev.translations || [])];
            const i = translations.findIndex((t) => t.langCode === langCode);
            if (i >= 0) translations[i] = { ...translations[i], title };
            else translations.push({ langCode, title });
            return { ...prev, translations };
        });
    };

    // Members as react-select options, in the stored order.
    const itemOptions = isBookSection
        ? (values.items || []).map((item) => {
            const id = item.bookId || item.seriesId;
            const cached = bookCache[id];
            if (item.seriesId) {
                return {
                    value: item.seriesId,
                    label: item.seriesName
                        ? (item.bookCount ? `${item.seriesName} — ${item.bookCount} books` : item.seriesName)
                        : cached?.label || "(unknown series)",
                    image: item.image || cached?.image,
                    isSeries: true,
                };
            }
            return {
                value: item.bookId,
                label: item.bookName
                    ? (item.authorName ? `${item.bookName} — ${item.authorName}` : item.bookName)
                    : cached?.label || "(unknown)",
                // The section GET already returns a cover per item; the search cache
                // only holds books looked up in this session, so relying on it alone
                // left every saved book showing a grey box.
                image: item.image || cached?.image,
            };
        })
        : (values.items || []).map((item) => {
        const rec = recommenders.find((r) => r.value === item.recommenderId);
        // Fall back to the loaded list for the label too, so a person never
        // shows blank if the section GET didn't echo recommenderName.
        return {
            value: item.recommenderId,
            label: item.recommenderName || rec?.label || "(unknown)",
            image: rec?.image,
        };
    });

    const setItemsFromOptions = (options) =>
        setValues((prev) => {
            const toItem = (o) =>
                prev.itemType === "book"
                    // Carry the cover through rather than leaning on bookCache: a
                    // book just picked should show its cover straight away, not
                    // only if the session happens to still hold the search result.
                    ? o.isSeries
                        ? { seriesId: o.value, seriesName: o.label, image: o.image }
                        : { bookId: o.value, bookName: o.label, image: o.image }
                    : { recommenderId: o.value, recommenderName: o.label, image: o.image };
            // A book section holds books and series, so an item is identified
            // by whichever id it carries.
            const idOf = (item) =>
                prev.itemType === "book"
                    ? item.bookId || item.seriesId
                    : item.recommenderId;

            const incoming = (options || []).map(toItem);
            const incomingIds = new Set(incoming.map(idOf));
            const existingIds = new Set((prev.items || []).map(idOf));

            // react-select appends a new pick to the end of its value array, which
            // on a long shelf drops it out of view — you search for a book, click
            // it, and nothing appears to happen. Newly picked items go to the top
            // instead; the rest keep whatever order was dragged into place.
            const added = incoming.filter((item) => !existingIds.has(idOf(item)));
            const kept = (prev.items || []).filter((item) => incomingIds.has(idOf(item)));

            return { ...prev, items: [...added, ...kept] };
        });

    // The sortable list hands back the exact order it wants — from a drag, the
    // position box or the top/bottom buttons. Nothing there is ever new, so the
    // order has to be taken literally. Routing it through setItemsFromOptions
    // silently reverted every reorder, because that one rebuilds the tail from
    // the previous order to keep newly picked items on top.
    //
    // Reordering the stored items by id, rather than rebuilding them from the
    // options, also keeps fields the options do not carry.
    const setItemsOrder = (options) =>
        setValues((prev) => {
            const idOf = (item) =>
                prev.itemType === "book"
                    ? item.bookId || item.seriesId
                    : item.recommenderId;
            const byId = new Map((prev.items || []).map((item) => [idOf(item), item]));
            return {
                ...prev,
                items: (options || []).map((o) => byId.get(o.value)).filter(Boolean),
            };
        });

    const validate = () => {
        const next = {};
        if (!titleForLang("en").trim()) next.title = "An English title is required — it's the fallback.";
        if (isAuto) {
            if (!Number.isInteger(values.minRecommenders) || values.minRecommenders < 0) {
                next.minRecommenders = "Enter a whole number, 0 or more.";
            }
        } else if (!values.items || values.items.length === 0) {
            next.items = isBookSection
                ? "Choose at least one book or series."
                : "Choose at least one person.";
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        try {
            setIsSubmit(true);
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const payload = {
                values: {
                    layout: values.layout,
                    translations: (values.translations || []).filter((t) => t.title && t.title.trim()),
                    // Banner shows one person; never send more.
                    itemType: values.itemType,
                    source: isAuto ? "mostRecommended" : "manual",
                    ...(isAuto && { minRecommenders: values.minRecommenders }),
                    // An automatic section's members are computed, so its preview
                    // is never sent back as a list to store.
                    ...(!isAuto && {
                        items: (isBanner ? values.items.slice(0, 1) : values.items).map((i) =>
                            isBookSection
                                ? (i.seriesId ? { seriesId: i.seriesId } : { bookId: i.bookId })
                                : { recommenderId: i.recommenderId }
                        ),
                    }),
                },
            };

            const url = isCreate
                ? `${API}/api/admin/picks/sections/add`
                : `${API}/api/admin/picks/sections/update/${sectionId}`;
            const { data: response } = await axios[isCreate ? "post" : "put"](url, payload, { headers });

            if (response.status) {
                onSuccess();
                handleClose();
                toast.success(isCreate ? "Section created." : "Section updated.");
            } else {
                toast.error(response.message || "Could not save this section.");
            }
        } catch (error) {
            console.error("handleSubmit catch error::", error);
            toast.error(error.response?.data?.error || error.response?.data?.message || "Could not save this section.");
        } finally {
            setIsSubmit(false);
        }
    };

    const handleDelete = async () => {
        const title = titleForLang("en") || "this section";
        const ok = await confirm({
            title: `Delete "${title}"?`,
            body: "It will be removed from the Picks tab. This can't be undone.",
            confirmLabel: "Delete",
        });
        if (!ok) return;

        setIsDeleting(true);
        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.put(
                `${API}/api/admin/picks/sections/delete/${sectionId}`,
                {},
                { headers }
            );
            if (response.status) {
                toast.success(`"${title}" removed.`);
                onSuccess();
                handleClose();
            } else {
                toast.error(response.message || "Could not remove the section.");
            }
        } catch (error) {
            console.error("handleDelete catch error::", error);
            toast.error(error.response?.data?.error || "Could not remove the section.");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleClose = () => {
        handleCloseModal();
        setErrors({});
        setValues(EMPTY);
    };

    const header = (
        <EditPageHeader
            title={isCreate ? "New Picks Section" : "Edit Picks Section"}
            subtitle={isCreate ? null : titleForLang("en")}
            actions={
                <>
                    {!isCreate && (
                        <HeaderDeleteButton onClick={handleDelete} disabled={isSubmit || isDeleting} />
                    )}
                    {asPage && (
                        <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={isSubmit}>
                            Back
                        </button>
                    )}
                </>
            }
        />
    );

    const body = (
        <div className={asPage ? styles.pageCard : "box_wrapper"}>
            {!asPage && header}

            <div className={asPage ? styles.pageContent : "modal_content_lg"}>
                    {/* Layout is fixed once created — it changes how the app renders. */}
                    {isCreate && (
                        <div className="mb-3">
                            <label className="label">Type:</label>
                            <div className="d-flex gap-2">
                                {["rail", "banner"].map((layout) => (
                                    <button
                                        key={layout}
                                        type="button"
                                        className={`btn btn-sm ${values.layout === layout ? "btn-dark" : "btn-outline-secondary"}`}
                                        onClick={() => setValues((prev) => ({ ...prev, layout }))}
                                    >
                                        {layout === "banner" ? "Banner (one hero card)" : "Rail (horizontal list)"}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Create-only, like layout: switching an existing section
                        between people and books would strand its members, and
                        the API refuses it without a replacement list. */}
                    {isCreate && (
                        <div className="mb-3">
                            <label className="label">Contains:</label>
                            <div className="d-flex gap-2">
                                {[
                                    { value: "recommender", label: "People" },
                                    { value: "book", label: "Books" },
                                ].map((opt) => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`btn btn-sm ${values.itemType === opt.value ? "btn-dark" : "btn-outline-secondary"}`}
                                        onClick={() => setValues((prev) => ({ ...prev, itemType: opt.value, items: [], source: "manual" }))}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            <small className="text-muted">
                                Books here are ones without a summary yet — use this for rails like "Top 10 Selling Books".
                            </small>
                        </div>
                    )}

                    {isAuto && (
                        <p className="text-muted small mb-3">
                            Automatic: books listed by more than the number of recommenders below that
                            don't have a summary yet. A random set is picked at the section's refresh
                            rate (set in the table), the same for every reader.
                        </p>
                    )}

                    <div className="mb-3">
                        <label className="label">Title:</label>
                        <div className="d-flex flex-wrap gap-1 mb-2">
                            {(languages || []).map((lang) => (
                                <button
                                    key={lang}
                                    type="button"
                                    className={`btn btn-sm ${titleLang === lang ? "btn-dark" : "btn-outline-secondary"}`}
                                    onClick={() => setTitleLang(lang)}
                                >
                                    {languageLabels?.[lang] || lang}{titleMissing(lang, titleForLang(lang), titleForLang("en")) ? " •" : ""}
                                </button>
                            ))}
                        </div>
                        <input
                            className="form_control"
                            placeholder={`Title in ${languageLabels?.[titleLang] || titleLang}...`}
                            type="text"
                            value={titleForLang(titleLang)}
                            onChange={(e) => handleChangeTitle(titleLang, e.target.value)}
                        />
                        <small className="text-muted">
                            {TITLE_HINT}
                        </small>
                        {errors.title && <p className="error-message d-block">{errors.title}</p>}
                    </div>

                    {isAuto ? (
                    <>
                        <div className="mb-3">
                            <label className="label">Recommended by more than:</label>
                            <input
                                className="form_control"
                                type="number"
                                min="0"
                                step="1"
                                value={values.minRecommenders ?? 5}
                                onChange={(e) =>
                                    setValues((prev) => ({ ...prev, minRecommenders: Number(e.target.value) }))
                                }
                            />
                            <small className="text-muted">
                                Number of recommenders. 5 means a book needs at least 6.
                            </small>
                            {errors.minRecommenders && (
                                <p className="error-message d-block">{errors.minRecommenders}</p>
                            )}
                        </div>
                        <div className="mb-3">
                            <label className="label">Today's books:</label>
                            {savedSource !== "mostRecommended" || !(values.items || []).length ? (
                                <p className="text-muted mb-0">
                                    {savedSource !== "mostRecommended"
                                        ? "Today's books appear here once the section is saved."
                                        : "No books qualify right now — try a lower number above, then save."}
                                </p>
                            ) : (
                                <div className={styles.autoPreview}>
                                    {values.items.map((item) => (
                                        <div
                                            key={item.bookId}
                                            className={styles.autoPreviewItem}
                                            title={item.authorName ? `${item.bookName} — ${item.authorName}` : item.bookName}
                                        >
                                            {item.image ? (
                                                <img src={item.image} alt={item.bookName} />
                                            ) : (
                                                <div className={styles.autoPreviewBlank} />
                                            )}
                                            <span>{item.authorName ? `${item.bookName} — ${item.authorName}` : item.bookName}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <small className="text-muted">
                                Picked by the server, so there is nothing to arrange. A change to the
                                number above shows here after saving.
                            </small>
                        </div>
                    </>
                    ) : (
                    <div className="mb-3">
                        <label className="label">
                            {isBanner
                                ? (isBookSection ? "Featured book:" : "Featured person:")
                                : (isBookSection ? "Books:" : "People:")}
                        </label>
                        {isBanner ? (
                            isBookSection ? (
                                <AsyncSelect
                                    className="form-control-md fw-medium p-0"
                                    isClearable
                                    cacheOptions
                                    defaultOptions={false}
                                    loadOptions={loadBookOptions}
                                    placeholder="Search a book by title..."
                                    noOptionsMessage={({ inputValue }) =>
                                        inputValue.trim().length < 2 ? "Type at least two letters" : "No books found"
                                    }
                                    value={itemOptions[0] ?? null}
                                    onChange={(selected) => setItemsFromOptions(selected ? [selected] : [])}
                                    components={SELECT_COMPONENTS}
                                />
                            ) : (
                            <Select
                                className="form-control-md fw-medium p-0"
                                isClearable
                                placeholder="Choose a person..."
                                value={itemOptions[0] ?? null}
                                onChange={(selected) => setItemsFromOptions(selected ? [selected] : [])}
                                options={recommenders}
                                components={SELECT_COMPONENTS}
                            />
                            )
                        ) : (
                            <>
                                {/* Pure add-picker: its own chips are hidden so the sortable
                                    list below is the single source of order and removal.
                                    Which is also why neither picker clears: with the chips
                                    hidden, react-select's cross sits in an empty-looking
                                    field and wipes every row in the list below it — a
                                    destructive control that reads as "clear what I typed".
                                    Removal belongs to the list, one row at a time —
                                    which is also why backspace is disarmed. On an empty
                                    input react-select pops the last value, and with the
                                    chips hidden the field gives no sign that backspace has
                                    anything to delete; a row would just vanish from the
                                    list below.

                                    The menu closes on a pick, too. It used to stay open so
                                    several could be added in a row, but with the chips
                                    hidden nothing in the field acknowledges a selection —
                                    and the open menu covers the list that would. It looked
                                    like the click had missed. */}
                                {isBookSection ? (
                                    <AsyncSelect
                                        className="form-control-md fw-medium p-0"
                                        isMulti
                                        markAdded
                                        isClearable={false}
                                        backspaceRemovesValue={false}
                                        cacheOptions
                                        defaultOptions={false}
                                        controlShouldRenderValue={false}
                                        loadOptions={loadBookOptions}
                                        placeholder="Search books by title..."
                                        noOptionsMessage={({ inputValue }) =>
                                            inputValue.trim().length < 2 ? "Type at least two letters" : "No books found"
                                        }
                                        value={itemOptions}
                                        onChange={setItemsFromOptions}
                                        components={SELECT_COMPONENTS}
                                    />
                                ) : (
                                    <Select
                                        className="form-control-md fw-medium p-0"
                                        isMulti
                                        markAdded
                                        isClearable={false}
                                        backspaceRemovesValue={false}
                                        controlShouldRenderValue={false}
                                        placeholder="Add people..."
                                        value={itemOptions}
                                        onChange={setItemsFromOptions}
                                        options={recommenders}
                                        components={SELECT_COMPONENTS}
                                    />
                                )}
                                <div className="mt-2">
                                    <SortablePeopleList
                                        items={itemOptions}
                                        onChange={setItemsOrder}
                                        thumbShape={isBookSection ? "cover" : "circle"}
                                        // One treatment for both: a card carrying its
                                        // position, its remove and its drag grip. People
                                        // used to be a plain row, which made the same
                                        // section look like two different screens
                                        // depending on what it held.
                                        variant="grid"
                                        emptyMessage={
                                            isBookSection
                                                ? "No books selected yet — search above."
                                                : undefined
                                        }
                                    />
                                </div>
                                <small className="text-muted">
                                    This is the order they appear in the app. Drag to reorder, or type a
                                    position to move a row straight there.
                                </small>
                            </>
                        )}
                        {errors.items && <p className="error-message d-block">{errors.items}</p>}
                    </div>
                    )}

                    {/* The banner's day-by-day schedule lives with the rest of the
                        banner, not as a second icon on the list. Only once saved:
                        the schedule is stored against the section's id. */}
                    {!isCreate && isBanner && (
                        <div className="mb-3">
                            {/* Who is featured each day, at a glance. Days left
                                empty show the featured pick above, dimmed. */}
                            <WeekSchedule
                                endpoint={`${API}/api/admin/picks/sections/${sectionId}/schedule`}
                                token={adminInfo?.token}
                                toItem={(item) => ({ id: item.recommenderId, name: item.recommenderName, image: item.image })}
                                fallback={itemOptions[0] ? { name: itemOptions[0].label, image: itemOptions[0].image } : null}
                                shape={isBookSection ? "cover" : "round"}
                                reloadKey={scheduleVersion}
                                onEdit={() => setEditingDay(new Date().toLocaleDateString("en-CA"))}
                                onSelectDay={setEditingDay}
                            />
                        </div>
                    )}

                </div>

            <div className="d-flex gap-2 justify-content-end mt-3">
                <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={isSubmit}>Cancel</button>
                <Button onClick={handleSubmit} disabled={isSubmit} isSubmit={isSubmit} label="Saving ..." text={isCreate ? "Create" : "Save"} />
            </div>
        </div>
    );

    // Mounted only while open, so its day state never outlives a visit.
    const schedule = editingDay && (
        <DayEditPopup
            key={sectionId}
            endpoint={`${API}/api/admin/picks/sections/${sectionId}/schedule`}
            initialDate={editingDay}
            options={recommenders}
            selectComponents={SELECT_COMPONENTS}
            toOption={(item) => ({ value: item.recommenderId, label: item.recommenderName, image: item.image })}
            toEntry={(option) => ({ recommenderIds: option ? [option.value] : [] })}
            defaultName={itemOptions[0]?.label}
            label="Person:"
            noun="person"
            onClose={(changed) => {
                setEditingDay(null);
                if (changed) setScheduleVersion((v) => v + 1);
            }}
        />
    );

    if (asPage) return <EditPageLayout>{header}{body}{schedule}</EditPageLayout>;

    return (
        <Modal isOpen={isVisible} onRequestClose={handleClose} className="modal_body_md" overlayClassName="modal_overlay">
            {body}
            {schedule}
        </Modal>
    );
};

export default AddEditPopup;
